import { useAuth } from '@/contexts/AuthContext';

/**
 * Single source of truth for enterprise UI gating.
 *
 * Switcher:               ONLY for OWNER / ORG_ADMIN with scope-expanded access.
 * Dashboard:              Any management role (OWNER/ORG_ADMIN/MANAGER) with
 *                         non-shop scope (organization/region/district).
 * In-Scope Talent (R73):  Any management role at ANY scope including shop —
 *                         shop managers can see talent in their own shop.
 *
 * Technicians and service advisors never see enterprise UI.
 */
export function useEnterpriseScope() {
  const { membership, hasScopeAccess } = useAuth();
  const role = membership?.role;
  const scopeType = (membership as any)?.scope_type as string | undefined;
  const scopeId = (membership as any)?.scope_id as string | null | undefined;

  const isManagementRole = role === 'OWNER' || role === 'ORG_ADMIN' || role === 'MANAGER';

  // Switcher: ONLY for OWNER / ORG_ADMIN with scope access (unchanged from I-A-04)
  const showShopSwitcher = hasScopeAccess &&
    (role === 'OWNER' || role === 'ORG_ADMIN');

  // Owners and org admins always see the org dashboard for their own org.
  // Managers need an explicit non-shop scope (district/region/org).
  const canViewOrgDashboard =
    role === 'OWNER' || role === 'ORG_ADMIN'
      ? true
      : hasScopeAccess &&
        role === 'MANAGER' &&
        (scopeType === 'organization' || scopeType === 'region' || scopeType === 'district');

  // R73 — Talent visibility: dedicated predicate, independent from org dashboard.
  // Includes shop-scoped managers so they can see techs in their own shop.
  const canViewInScopeTalent = isManagementRole &&
    (scopeType === 'shop' || scopeType === 'district' || scopeType === 'region' || scopeType === 'organization');

  // Dynamic label for dashboard header
  const scopeLabel = scopeType === 'shop' ? 'Shop'
    : scopeType === 'region' ? 'Region'
    : scopeType === 'district' ? 'District'
    : 'Organization';

  return {
    showShopSwitcher,
    canViewOrgDashboard,
    canViewInScopeTalent,
    scopeLabel,
    scopeType: scopeType ?? null,
    scopeId: scopeId ?? null,
  };
}
